import { Service } from 'typedi';
import { makeAutoObservable, toJS } from 'mobx';
import { GetCombinedItemQuery, CombinedItem } from '../core/models';
import { isEqual } from 'lodash';
import { AppStore } from './app.store';
import { CombinedItemService } from '../services/combinedItem.service';
import { NotificationContent, Notifications } from '../pages/Notifications';
import {
    BulkUpdateCombinedItemQueryUpdate,
    BulkUpdateCombinedItemType,
} from '../core/models/datasource/combinedItems/parts/bulkUpdateCombinedItemQueryUpdate';
import moment from 'moment/moment';
import { DetailedCombinedItem } from '../core/models/datasource/combinedItems/detailedCombinedItem';

export interface CombinedItemEditQuery {
    id?: string;
    description?: string;
    practice?: string;
}

export enum CombinedItemBulkUpdateType {
    AddToPractices = 'addToPractices',
    Description = 'description',
    HideZeroAmounts = 'hideZeroAmounts',
}

export interface CombinedItemsSelectionModel {
    items?: Map<string, CombinedItem>;
    excluded?: Map<string, CombinedItem>;
    all?: boolean;
    total?: number;
    groups?: number;
}

export interface CombinedItemsPaginationModel {
    total?: number;
}

export interface UpdateCombinedItems {
    items: Partial<CombinedItem>[];
    type: BulkUpdateCombinedItemType;
    runAt?: Date;
    message: NotificationContent;
    name?: string;
}

export interface CombinedItemGridState {
    widthModel?: Record<number | string, number>;
    pinned?: {
        left?: string[];
        right?: string[];
    };
    groupingModel?: string[];
    visibilityModel?: Record<number | string, boolean | undefined>;
    sortModel?: {
        field: string;
        sort: 'asc' | 'desc' | null | undefined;
    }[];
    orderModel?: Record<number | string, number>;
}

export interface CombinedItemState {
    grid?: CombinedItemGridState;
}

export const COMBINED_ITEMS_AUTO_COMPLETE_SEARCH_SIZE = 20;

@Service()
export class CombinedItemStore {
    private _isLoadingItems = false;
    private _isLoadingDetailedItems = 0;
    private _items?: CombinedItem[];
    private _detailedItems?: Map<string, DetailedCombinedItem> = new Map();
    private _selectionModel?: CombinedItemsSelectionModel;
    private _pagingModel?: CombinedItemsPaginationModel;
    private _currentSearchQuery?: GetCombinedItemQuery;

    get isLoadingItems(): boolean {
        return this._isLoadingItems;
    }

    get isLoadingDetailedItems(): boolean {
        return Boolean(this._isLoadingDetailedItems);
    }

    get items(): CombinedItem[] | undefined {
        return this._items && toJS(this._items);
    }

    get detailedItems(): Map<string, DetailedCombinedItem> | undefined {
        return this._detailedItems && toJS(this._detailedItems);
    }

    get selectionModel(): CombinedItemsSelectionModel | undefined {
        return toJS(this._selectionModel);
    }

    get pagingModel(): CombinedItemsPaginationModel | undefined {
        return toJS(this._pagingModel);
    }

    get selectedItems(): CombinedItem[] | undefined {
        if (this._selectionModel?.all) {
            return toJS(this._items);
        }

        return this._selectionModel?.items && [...toJS(this._selectionModel?.items)?.values()];
    }

    get columnsState(): CombinedItemGridState | undefined {
        return toJS(this.appStore.userState?.combinedItems?.grid);
    }

    get detailsPanelState(): CombinedItemState | undefined {
        return toJS(this.appStore.userState?.combinedItemsDetails);
    }

    get currentSearchQuery(): GetCombinedItemQuery | undefined {
        return toJS(this._currentSearchQuery);
    }

    constructor(private combinedItemService: CombinedItemService, private appStore: AppStore) {
        makeAutoObservable(this);
    }

    getRowId = (row: CombinedItem): string => {
        const { pimsGeneratedId, description, provider } = row;
        return `${pimsGeneratedId}-${description}-${provider.siteId}`;
    };

    updateCurrentSearchQuery = (query: GetCombinedItemQuery): void => {
        this._currentSearchQuery = { ...query };
    };

    updateColumnsState = (state: CombinedItemGridState) => {
        const currentState = toJS(this.appStore.userState?.combinedItems);
        if (isEqual(currentState?.grid, state)) {
            return;
        }

        const invoiceItemsState = { combinedItems: { ...currentState, grid: state } };
        this.appStore.updateUserState(invoiceItemsState);
    };

    updateDetailsPanelState = (state: CombinedItemState) => {
        const currentState = toJS(this.appStore.userState?.combinedItemsDetails);
        if (isEqual(currentState, state)) {
            return;
        }

        const invoiceItemsState = { combinedItemsDetails: state };
        this.appStore.updateUserState(invoiceItemsState);
    };

    fetchItems = async (query: GetCombinedItemQuery = {}): Promise<void> => {
        if (this._isLoadingItems) {
            return;
        }

        try {
            this._isLoadingItems = true;

            const { data, errors } = await this.combinedItemService.getItems({ ...query });

            this._items = [];

            if (!errors.length) {
                this._items = data;
                this.updateSelectionModel(undefined);

                this._pagingModel = {
                    total: data?.length,
                };
            }
        } finally {
            this._isLoadingItems = false;
        }
    };

    async fetchItemsForAutoComplete(query: GetCombinedItemQuery = {}): Promise<CombinedItem[]> {
        const { data } = await this.combinedItemService.getItems({ ...query, size: COMBINED_ITEMS_AUTO_COMPLETE_SEARCH_SIZE, page: 0 });
        return data;
    }

    fetchDetailedItems = async (item: CombinedItem): Promise<DetailedCombinedItem[]> => {
        try {
            this._isLoadingDetailedItems += 1;

            const query: Omit<GetCombinedItemQuery, 'depth'> = { pimsGeneratedId: item.pimsGeneratedId, siteId: item.provider?.siteId };
            const { data, errors } = await this.combinedItemService.getItems<DetailedCombinedItem>({ ...query, depth: 1 });

            if (!errors.length) {
                data.forEach((item) => {
                    this._detailedItems?.set(this.getRowId(item), item);
                });
            } else {
                Notifications.fromException(errors);
                console.error(errors);
            }

            return data;
        } finally {
            this._isLoadingDetailedItems -= 1;
        }
    };

    updateSelectionModel = (params?: CombinedItemsSelectionModel): void => {
        if (this._selectionModel?.all && params?.items && params.all === undefined) {
            const excluded = new Map<string, CombinedItem>();

            this._items?.forEach((item) => {
                const { pimsGeneratedId } = item;
                const key = `${pimsGeneratedId}`;

                if (!params.items?.has(key)) {
                    excluded.set(key, item);
                }
            });

            this._selectionModel = {
                all: true,
                items: this._selectionModel.items,
                excluded,
            };
        } else {
            this._selectionModel = {
                all: params?.all,
                items: params?.items,
                excluded: undefined,
            };
        }
    };

    saveItems = async (props: UpdateCombinedItems): Promise<void> => {
        const { items, type, runAt = new Date(), message, name = 'Add to Additional Practices - Combi Products' } = props;
        const itemUpdate: BulkUpdateCombinedItemQueryUpdate[] = items.map((item) => ({
            data: item,
            type,
        }));

        try {
            await this.combinedItemService.createBulkUpdate({ runAt: moment(runAt).format('YYYY-MM-DD HH:mm:ssZ'), name, itemUpdate });
            Notifications.success(message);
        } catch (error) {
            Notifications.fromException(error);
        }
    };
}
